/**
 * AST-Based Code Analyzer
 * 
 * THE MOST COMPREHENSIVE HALLUCINATION DETECTION:
 * - Deep AST parsing for structural analysis
 * - Token-level validation
 * - Scope analysis for variable/function resolution
 * - Type inference and checking
 * - Import graph building and validation
 * - Dead code and unreachable path detection
 */

import * as fs from 'fs';
import * as path from 'path';

export interface ASTNode {
  type: string;
  name?: string;
  value?: any;
  children?: ASTNode[];
  location: { start: number; end: number; line: number; column: number };
  scope?: ScopeInfo;
  metadata?: Record<string, any>;
}

export interface ScopeInfo {
  id: string;
  type: 'global' | 'function' | 'block' | 'class' | 'module';
  parent?: string;
  variables: Map<string, VariableInfo>;
  functions: Map<string, FunctionInfo>;
  types: Map<string, ASTTypeInfo>;
  imports: Map<string, ASTImportInfo>;
}

export interface VariableInfo {
  name: string;
  type: string;
  definedAt: number;
  usedAt: number[];
  isConst: boolean;
  isExported: boolean;
  value?: any;
}

export interface FunctionInfo {
  name: string;
  params: ParamInfo[];
  returnType: string;
  isAsync: boolean;
  isGenerator: boolean;
  isExported: boolean;
  definedAt: number;
  calledAt: number[];
  complexity: number;
}

export interface ParamInfo {
  name: string;
  type: string;
  optional: boolean;
  defaultValue?: string;
  rest: boolean;
}

export interface ASTTypeInfo {
  name: string;
  kind: 'interface' | 'type' | 'class' | 'enum';
  properties: Map<string, { type: string; optional: boolean }>;
  methods: Map<string, FunctionInfo>;
  extends?: string[];
  implements?: string[];
}

export interface ASTImportInfo {
  source: string;
  specifiers: { name: string; alias?: string; isDefault: boolean; isNamespace: boolean }[];
  isTypeOnly: boolean;
  resolvedPath?: string;
  exists: boolean;
}

export interface ASTAnalysisResult {
  isValid: boolean;
  errors: ASTError[];
  warnings: ASTWarning[];
  scopes: Map<string, ScopeInfo>;
  imports: ASTImportInfo[];
  exports: string[];
  unresolvedReferences: UnresolvedReference[];
  suspiciousPatterns: SuspiciousPattern[];
  metrics: CodeMetrics;
}

export interface ASTError {
  type: string;
  message: string;
  location: { line: number; column: number };
  severity: 'error' | 'critical';
  fix?: string;
}

export interface ASTWarning {
  type: string;
  message: string;
  location: { line: number; column: number };
  confidence: number;
}

export interface UnresolvedReference {
  name: string;
  type: 'variable' | 'function' | 'type' | 'import';
  location: { line: number; column: number };
  possibleMatches: string[];
}

export interface SuspiciousPattern {
  pattern: string;
  description: string;
  location: { line: number; column: number };
  confidence: number;
  category: 'hallucination' | 'bug' | 'smell' | 'security';
}

export interface CodeMetrics {
  lines: number;
  statements: number;
  functions: number;
  classes: number;
  imports: number;
  exports: number;
  complexity: number;
  depth: number;
  maintainabilityIndex: number;
}

export class ASTAnalyzer {
  private workspaceRoot: string;
  private scopeStack: ScopeInfo[] = [];
  private globalScope: ScopeInfo;
  private currentScopeId = 0;
  private knownGlobals: Set<string>;
  private knownBuiltins: Map<string, Set<string>>;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.globalScope = this.createScope('global');
    this.knownGlobals = this.initializeGlobals();
    this.knownBuiltins = this.initializeBuiltins();
  }

  /**
   * Analyze code and return comprehensive results
   */
  analyze(code: string, language: string = 'typescript'): ASTAnalysisResult {
    const errors: ASTError[] = [];
    const warnings: ASTWarning[] = [];
    const scopes = new Map<string, ScopeInfo>();
    const imports: ASTImportInfo[] = [];
    const exports: string[] = [];
    const unresolvedReferences: UnresolvedReference[] = [];
    const suspiciousPatterns: SuspiciousPattern[] = [];

    // Reset state
    this.scopeStack = [this.globalScope];
    scopes.set(this.globalScope.id, this.globalScope);

    const lines = code.split('\n');

    // Phase 1: Extract imports and build import graph
    this.extractImports(code, imports, errors);

    // Phase 2: Extract declarations (variables, functions, types)
    this.extractDeclarations(code, lines, scopes, exports);

    // Phase 3: Analyze references and resolve them
    this.analyzeReferences(code, lines, scopes, unresolvedReferences, errors);

    // Phase 4: Detect suspicious patterns
    this.detectSuspiciousPatterns(code, lines, suspiciousPatterns, warnings);

    // Phase 5: Validate type consistency
    this.validateTypes(code, lines, scopes, errors, warnings);

    // Phase 6: Check for common hallucination patterns
    this.checkHallucinationPatterns(code, lines, errors, warnings, suspiciousPatterns);

    // Calculate metrics
    const metrics = this.calculateMetrics(code, lines, scopes);

    return {
      isValid: errors.filter(e => e.severity === 'critical').length === 0,
      errors,
      warnings,
      scopes,
      imports,
      exports,
      unresolvedReferences,
      suspiciousPatterns,
      metrics,
    };
  }

  /**
   * Extract and validate imports
   */
  private extractImports(code: string, imports: ASTImportInfo[], errors: ASTError[]): void {
    const importRegex = /import\s+(?:type\s+)?(?:(\{[^}]+\})|(\*\s+as\s+\w+)|(\w+))?\s*(?:,\s*(?:(\{[^}]+\})|(\w+)))?\s*from\s+['"]([^'"]+)['"]/g;
    
    let match;
    while ((match = importRegex.exec(code)) !== null) {
      const [fullMatch, namedImports, namespaceImport, defaultImport, additionalNamed, additionalDefault, source] = match;
      const isTypeOnly = fullMatch.includes('import type');
      
      const specifiers: ASTImportInfo['specifiers'] = [];

      // Parse named imports
      if (namedImports) {
        const names = namedImports.replace(/[{}]/g, '').split(',').map(s => s.trim());
        for (const name of names) {
          if (name) {
            const [importName, alias] = name.split(/\s+as\s+/).map(s => s.trim());
            specifiers.push({ name: importName, alias, isDefault: false, isNamespace: false });
          }
        }
      }

      // Parse namespace import
      if (namespaceImport) {
        const alias = namespaceImport.replace('* as', '').trim();
        specifiers.push({ name: '*', alias, isDefault: false, isNamespace: true });
      }

      // Parse default import
      if (defaultImport) {
        specifiers.push({ name: defaultImport, isDefault: true, isNamespace: false });
      }

      // Validate import source
      const importInfo: ASTImportInfo = {
        source,
        specifiers,
        isTypeOnly,
        exists: this.validateImportSource(source),
      };

      // Check for suspicious import patterns
      if (this.isSuspiciousImport(source)) {
        errors.push({
          type: 'suspicious_import',
          message: `Suspicious import source: "${source}" - may be hallucinated`,
          location: this.getLocation(code, match.index),
          severity: 'error',
          fix: `Verify this package exists: npm info ${source}`,
        });
      }

      imports.push(importInfo);
    }
  }

  /**
   * Extract declarations from code
   */
  private extractDeclarations(
    code: string,
    lines: string[],
    scopes: Map<string, ScopeInfo>,
    exports: string[]
  ): void {
    // Extract variable declarations
    const varRegex = /(?:export\s+)?(?:const|let|var)\s+(\w+)(?:\s*:\s*([^=]+))?\s*=/g;
    let match;
    while ((match = varRegex.exec(code)) !== null) {
      const [fullMatch, name, type] = match;
      const isExported = fullMatch.startsWith('export');
      const isConst = fullMatch.includes('const');
      
      this.globalScope.variables.set(name, {
        name,
        type: type?.trim() || 'unknown',
        definedAt: this.getLineNumber(code, match.index),
        usedAt: [],
        isConst,
        isExported,
      });

      if (isExported) exports.push(name);
    }

    // Extract function declarations
    const funcRegex = /(?:export\s+)?(?:async\s+)?function\s*(\*?)\s*(\w+)\s*\(([^)]*)\)(?:\s*:\s*([^\{]+))?\s*\{/g;
    while ((match = funcRegex.exec(code)) !== null) {
      const [fullMatch, generator, name, params, returnType] = match;
      const isExported = fullMatch.startsWith('export');
      const isAsync = fullMatch.includes('async');
      const isGenerator = generator === '*';

      this.globalScope.functions.set(name, {
        name,
        params: this.parseParams(params),
        returnType: returnType?.trim() || 'void',
        isAsync,
        isGenerator,
        isExported,
        definedAt: this.getLineNumber(code, match.index),
        calledAt: [],
        complexity: this.calculateFunctionComplexity(code, match.index),
      });

      if (isExported) exports.push(name);
    }

    // Extract arrow function assignments
    const arrowRegex = /(?:export\s+)?(?:const|let)\s+(\w+)\s*(?::\s*([^=]+))?\s*=\s*(?:async\s*)?\([^)]*\)\s*(?::\s*([^\s=>]+))?\s*=>/g;
    while ((match = arrowRegex.exec(code)) !== null) {
      const [fullMatch, name, typeAnnotation, returnType] = match;
      const isExported = fullMatch.startsWith('export');
      const isAsync = fullMatch.includes('async');

      this.globalScope.functions.set(name, {
        name,
        params: [],
        returnType: returnType?.trim() || typeAnnotation?.trim() || 'unknown',
        isAsync,
        isGenerator: false,
        isExported,
        definedAt: this.getLineNumber(code, match.index),
        calledAt: [],
        complexity: 1,
      });

      if (isExported) exports.push(name);
    }

    // Extract type/interface declarations
    const typeRegex = /(?:export\s+)?(?:interface|type)\s+(\w+)(?:<[^>]+>)?\s*(?:extends\s+([^{]+))?\s*[={]/g;
    while ((match = typeRegex.exec(code)) !== null) {
      const [fullMatch, name, extendsClause] = match;
      const isExported = fullMatch.startsWith('export');
      const kind = fullMatch.includes('interface') ? 'interface' : 'type';

      this.globalScope.types.set(name, {
        name,
        kind: kind as 'interface' | 'type',
        properties: new Map(),
        methods: new Map(),
        extends: extendsClause?.split(',').map(s => s.trim()),
      });

      if (isExported) exports.push(name);
    }

    // Extract class declarations
    const classRegex = /(?:export\s+)?class\s+(\w+)(?:<[^>]+>)?(?:\s+extends\s+(\w+))?(?:\s+implements\s+([^{]+))?\s*\{/g;
    while ((match = classRegex.exec(code)) !== null) {
      const [fullMatch, name, extendsClass, implementsClause] = match;
      const isExported = fullMatch.startsWith('export');

      this.globalScope.types.set(name, {
        name,
        kind: 'class',
        properties: new Map(),
        methods: new Map(),
        extends: extendsClass ? [extendsClass] : undefined,
        implements: implementsClause?.split(',').map(s => s.trim()),
      });

      if (isExported) exports.push(name);
    }
  }

  /**
   * Analyze references and find unresolved ones
   */
  private analyzeReferences(
    code: string,
    lines: string[],
    scopes: Map<string, ScopeInfo>,
    unresolvedReferences: UnresolvedReference[],
    errors: ASTError[]
  ): void {
    // Find all identifiers used in the code
    const identifierRegex = /\b([A-Z][a-zA-Z0-9]*|[a-z][a-zA-Z0-9]*)\b/g;
    const usedIdentifiers = new Set<string>();
    
    let match;
    while ((match = identifierRegex.exec(code)) !== null) {
      usedIdentifiers.add(match[1]);
    }

    // Check each identifier against known declarations
    for (const identifier of usedIdentifiers) {
      // Skip keywords and known globals
      if (this.isKeyword(identifier) || this.knownGlobals.has(identifier)) {
        continue;
      }

      // Check if it's defined in any scope
      let found = false;
      for (const scope of scopes.values()) {
        if (scope.variables.has(identifier) || 
            scope.functions.has(identifier) || 
            scope.types.has(identifier)) {
          found = true;
          break;
        }
        // Check imports
        for (const imp of scope.imports.values()) {
          if (imp.specifiers.some(s => s.name === identifier || s.alias === identifier)) {
            found = true;
            break;
          }
        }
      }

      if (!found && identifier.length > 1) {
        // Find possible matches using fuzzy matching
        const possibleMatches = this.findSimilarIdentifiers(identifier, scopes);
        
        // Only report if it looks like it should be defined
        if (this.looksLikeReference(code, identifier)) {
          unresolvedReferences.push({
            name: identifier,
            type: this.guessReferenceType(code, identifier),
            location: this.getLocation(code, code.indexOf(identifier)),
            possibleMatches,
          });
        }
      }
    }
  }

  /**
   * Detect suspicious patterns that may indicate hallucination
   */
  private detectSuspiciousPatterns(
    code: string,
    lines: string[],
    suspiciousPatterns: SuspiciousPattern[],
    warnings: ASTWarning[]
  ): void {
    const patterns = [
      // Suspicious API calls
      { regex: /\.\s*(toJSON|fromJSON)\s*\(\s*\)/g, desc: 'Non-standard JSON method', category: 'hallucination' as const },
      { regex: /Array\.(from|of|isArray)\s*\([^)]*\)\.(contains|remove|add)\s*\(/g, desc: 'Chained non-existent Array method', category: 'hallucination' as const },
      { regex: /Object\.(keys|values|entries)\s*\([^)]*\)\.(contains|remove|add)\s*\(/g, desc: 'Chained non-existent Object method', category: 'hallucination' as const },
      
      // Mixing paradigms
      { regex: /await\s+\w+\.then\s*\(/g, desc: 'Mixing await with .then()', category: 'bug' as const },
      { regex: /new\s+Promise\s*\(\s*async/g, desc: 'Async function in Promise constructor', category: 'bug' as const },
      
      // Type issues
      { regex: /:\s*any\[\]/g, desc: 'Using any[] type', category: 'smell' as const },
      { regex: /as\s+any\b/g, desc: 'Type assertion to any', category: 'smell' as const },
      { regex: /@ts-ignore/g, desc: 'TypeScript ignore comment', category: 'smell' as const },
      
      // Security issues
      { regex: /eval\s*\(/g, desc: 'Use of eval()', category: 'security' as const },
      { regex: /innerHTML\s*=/g, desc: 'Direct innerHTML assignment', category: 'security' as const },
      { regex: /document\.write\s*\(/g, desc: 'Use of document.write()', category: 'security' as const },
      
      // Hallucinated hooks
      { regex: /use(Fetch|Data|API|Query|Cache|Auth|Session)\s*\(/g, desc: 'Potentially non-existent React hook', category: 'hallucination' as const },
      
      // Suspicious method chains
      { regex: /\.\s*map\s*\([^)]+\)\s*\.\s*flat\s*\(\s*\)\s*\.\s*unique\s*\(/g, desc: 'Non-existent .unique() method', category: 'hallucination' as const },
      { regex: /\.\s*filter\s*\([^)]+\)\s*\.\s*first\s*\(/g, desc: 'Non-existent .first() method', category: 'hallucination' as const },
      { regex: /\.\s*find\s*\([^)]+\)\s*\.\s*orElse\s*\(/g, desc: 'Non-existent .orElse() method', category: 'hallucination' as const },
    ];

    for (const { regex, desc, category } of patterns) {
      let match;
      while ((match = regex.exec(code)) !== null) {
        suspiciousPatterns.push({
          pattern: match[0],
          description: desc,
          location: this.getLocation(code, match.index),
          confidence: 0.8,
          category,
        });
      }
    }
  }

  /**
   * Validate type consistency
   */
  private validateTypes(
    code: string,
    lines: string[],
    scopes: Map<string, ScopeInfo>,
    errors: ASTError[],
    warnings: ASTWarning[]
  ): void {
    // Check for type mismatches in assignments
    const assignmentRegex = /(\w+)\s*=\s*([^;]+);/g;
    let match;
    
    while ((match = assignmentRegex.exec(code)) !== null) {
      const [, varName, value] = match;
      const varInfo = this.globalScope.variables.get(varName);
      
      if (varInfo && varInfo.type !== 'unknown') {
        const inferredType = this.inferType(value);
        if (inferredType !== 'unknown' && !this.typesCompatible(varInfo.type, inferredType)) {
          warnings.push({
            type: 'type_mismatch',
            message: `Possible type mismatch: ${varName} is ${varInfo.type} but assigned ${inferredType}`,
            location: this.getLocation(code, match.index),
            confidence: 0.7,
          });
        }
      }
    }
  }

  /**
   * Check for common hallucination patterns
   */
  private checkHallucinationPatterns(
    code: string,
    lines: string[],
    errors: ASTError[],
    warnings: ASTWarning[],
    suspiciousPatterns: SuspiciousPattern[]
  ): void {
    // Check for invented methods on built-in objects
    const builtinMethodChecks = [
      { obj: 'Array', fake: ['contains', 'remove', 'add', 'delete', 'insert', 'append', 'prepend', 'first', 'last', 'empty', 'clear', 'size', 'count', 'get', 'set', 'has', 'unique', 'distinct', 'compact', 'flatten', 'pluck', 'where', 'select', 'groupBy', 'orderBy', 'take', 'skip', 'any', 'all', 'none'] },
      { obj: 'String', fake: ['reverse', 'capitalize', 'camelCase', 'snakeCase', 'kebabCase', 'count', 'isEmpty', 'isBlank', 'format', 'printf', 'center', 'ljust', 'rjust', 'zfill', 'isNumeric', 'isAlpha', 'isAlphaNumeric', 'words', 'lines', 'chars', 'bytes', 'toArray', 'truncate', 'excerpt'] },
      { obj: 'Object', fake: ['length', 'size', 'isEmpty', 'isNotEmpty', 'merge', 'deepMerge', 'clone', 'deepClone', 'equals', 'deepEquals', 'get', 'set', 'has', 'delete', 'forEach', 'map', 'filter', 'reduce', 'pick', 'omit', 'invert', 'defaults'] },
      { obj: 'Promise', fake: ['wait', 'delay', 'sleep', 'timeout', 'retry', 'parallel', 'serial', 'map', 'filter', 'reduce', 'some', 'every', 'first', 'last', 'props', 'settle'] },
      { obj: 'Number', fake: ['isPositive', 'isNegative', 'isZero', 'isEven', 'isOdd', 'isPrime', 'abs', 'round', 'floor', 'ceil', 'clamp', 'between', 'times', 'upto', 'downto'] },
      { obj: 'Date', fake: ['format', 'addDays', 'addMonths', 'addYears', 'addHours', 'addMinutes', 'addSeconds', 'subtract', 'diff', 'isAfter', 'isBefore', 'isBetween', 'startOf', 'endOf', 'isToday', 'isTomorrow', 'isYesterday'] },
      { obj: 'Math', fake: ['clamp', 'lerp', 'map', 'constrain', 'normalize', 'distance', 'angle', 'degrees', 'radians', 'factorial', 'fibonacci', 'isPrime', 'gcd', 'lcm'] },
      { obj: 'JSON', fake: ['isValid', 'tryParse', 'prettify', 'minify', 'merge', 'diff', 'patch', 'clone', 'equals'] },
      { obj: 'console', fake: ['write', 'writeln', 'print', 'println', 'printf', 'debug', 'verbose', 'notice', 'critical', 'emergency', 'success', 'failure', 'color', 'style'] },
      { obj: 'window', fake: ['wait', 'delay', 'sleep', 'navigate', 'redirect', 'reload', 'refresh', 'back', 'forward', 'close', 'minimize', 'maximize', 'resize', 'move', 'center'] },
      { obj: 'document', fake: ['find', 'findAll', 'select', 'selectAll', 'get', 'getAll', 'query', 'queryAll', 'on', 'off', 'emit', 'trigger', 'ready', 'load', 'unload'] },
      { obj: 'fetch', fake: ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'json', 'text', 'blob', 'form', 'timeout', 'retry', 'cancel', 'abort'] },
      { obj: 'localStorage', fake: ['get', 'set', 'has', 'delete', 'keys', 'values', 'entries', 'forEach', 'map', 'filter', 'find', 'size', 'isEmpty', 'expire'] },
      { obj: 'sessionStorage', fake: ['get', 'set', 'has', 'delete', 'keys', 'values', 'entries', 'forEach', 'map', 'filter', 'find', 'size', 'isEmpty', 'expire'] },
    ];

    for (const { obj, fake } of builtinMethodChecks) {
      for (const method of fake) {
        const regex = new RegExp(`\\b${obj}\\s*\\.\\s*${method}\\s*\\(`, 'g');
        let match;
        while ((match = regex.exec(code)) !== null) {
          errors.push({
            type: 'hallucinated_method',
            message: `${obj}.${method}() does not exist - this is a hallucination`,
            location: this.getLocation(code, match.index),
            severity: 'error',
            fix: this.suggestAlternative(obj, method),
          });
        }
      }
    }

    // Check for hallucinated Node.js APIs
    const nodeAPIChecks = [
      { module: 'fs', fake: ['read', 'write', 'delete', 'remove', 'copy', 'move', 'rename', 'create', 'open', 'close', 'seek', 'tell', 'flush', 'sync', 'list', 'walk', 'glob', 'find', 'search', 'watch', 'touch', 'chmod', 'chown'] },
      { module: 'path', fake: ['exists', 'isFile', 'isDir', 'isDirectory', 'isAbsolute', 'isRelative', 'absolute', 'relative', 'parent', 'filename', 'name', 'stem', 'suffix', 'suffixes', 'parts', 'anchor', 'drive', 'root'] },
      { module: 'http', fake: ['fetch', 'get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'send', 'receive', 'listen', 'connect', 'disconnect', 'close', 'abort', 'timeout', 'retry'] },
      { module: 'crypto', fake: ['hash', 'encrypt', 'decrypt', 'sign', 'verify', 'generate', 'random', 'uuid', 'md5', 'sha1', 'sha256', 'sha512', 'hmac', 'aes', 'rsa', 'ecdsa'] },
      { module: 'util', fake: ['print', 'println', 'printf', 'format', 'log', 'error', 'warn', 'info', 'debug', 'trace', 'assert', 'check', 'validate', 'verify', 'test', 'bench', 'measure'] },
    ];

    for (const { module: mod, fake } of nodeAPIChecks) {
      for (const method of fake) {
        const regex = new RegExp(`\\b${mod}\\s*\\.\\s*${method}\\s*\\(`, 'g');
        let match;
        while ((match = regex.exec(code)) !== null) {
          warnings.push({
            type: 'suspicious_api',
            message: `${mod}.${method}() may not exist - verify this API`,
            location: this.getLocation(code, match.index),
            confidence: 0.85,
          });
        }
      }
    }
  }

  /**
   * Calculate code metrics
   */
  private calculateMetrics(code: string, lines: string[], scopes: Map<string, ScopeInfo>): CodeMetrics {
    const nonEmptyLines = lines.filter(l => l.trim()).length;
    const statements = (code.match(/;/g) || []).length;
    const functions = this.globalScope.functions.size;
    const classes = Array.from(this.globalScope.types.values()).filter(t => t.kind === 'class').length;
    const imports = this.globalScope.imports.size;
    const exports = Array.from(this.globalScope.variables.values()).filter(v => v.isExported).length +
                   Array.from(this.globalScope.functions.values()).filter(f => f.isExported).length;

    // Calculate cyclomatic complexity
    let complexity = 1;
    const decisionPoints = ['if', 'else if', 'for', 'while', 'case', 'catch', '&&', '||', '?'];
    for (const point of decisionPoints) {
      const regex = new RegExp(`\\b${point.replace(/[|&?]/g, '\\$&')}\\b`, 'g');
      complexity += (code.match(regex) || []).length;
    }

    // Calculate max nesting depth
    let maxDepth = 0;
    let currentDepth = 0;
    for (const char of code) {
      if (char === '{') {
        currentDepth++;
        maxDepth = Math.max(maxDepth, currentDepth);
      } else if (char === '}') {
        currentDepth = Math.max(0, currentDepth - 1);
      }
    }

    // Calculate maintainability index (simplified)
    const halsteadVolume = nonEmptyLines * Math.log2(statements + 1);
    const maintainabilityIndex = Math.max(0, 171 - 5.2 * Math.log(halsteadVolume) - 0.23 * complexity - 16.2 * Math.log(nonEmptyLines));

    return {
      lines: lines.length,
      statements,
      functions,
      classes,
      imports,
      exports,
      complexity,
      depth: maxDepth,
      maintainabilityIndex: Math.round(maintainabilityIndex),
    };
  }

  // Helper methods

  private createScope(type: ScopeInfo['type']): ScopeInfo {
    return {
      id: `scope-${this.currentScopeId++}`,
      type,
      variables: new Map(),
      functions: new Map(),
      types: new Map(),
      imports: new Map(),
    };
  }

  private initializeGlobals(): Set<string> {
    return new Set([
      // JavaScript globals
      'undefined', 'null', 'NaN', 'Infinity', 'globalThis', 'window', 'document', 'navigator',
      'console', 'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval', 'setImmediate',
      'queueMicrotask', 'requestAnimationFrame', 'cancelAnimationFrame', 'fetch', 'Response',
      'Request', 'Headers', 'URL', 'URLSearchParams', 'FormData', 'Blob', 'File', 'FileReader',
      'ArrayBuffer', 'DataView', 'Int8Array', 'Uint8Array', 'Int16Array', 'Uint16Array',
      'Int32Array', 'Uint32Array', 'Float32Array', 'Float64Array', 'BigInt64Array', 'BigUint64Array',
      'Map', 'Set', 'WeakMap', 'WeakSet', 'Symbol', 'Proxy', 'Reflect', 'Promise', 'Generator',
      'Error', 'TypeError', 'ReferenceError', 'SyntaxError', 'RangeError', 'EvalError', 'URIError',
      'JSON', 'Math', 'Date', 'RegExp', 'Array', 'Object', 'String', 'Number', 'Boolean', 'BigInt',
      'Function', 'eval', 'isFinite', 'isNaN', 'parseFloat', 'parseInt', 'decodeURI', 'decodeURIComponent',
      'encodeURI', 'encodeURIComponent', 'escape', 'unescape', 'alert', 'confirm', 'prompt',
      // Node.js globals
      'process', 'Buffer', '__dirname', '__filename', 'module', 'exports', 'require', 'global',
      // TypeScript
      'unknown', 'never', 'any', 'void', 'this', 'super', 'arguments',
      // Common types
      'Partial', 'Required', 'Readonly', 'Record', 'Pick', 'Omit', 'Exclude', 'Extract',
      'NonNullable', 'Parameters', 'ConstructorParameters', 'ReturnType', 'InstanceType',
      'ThisParameterType', 'OmitThisParameter', 'ThisType', 'Awaited',
      // React
      'React', 'ReactDOM', 'useState', 'useEffect', 'useContext', 'useReducer', 'useCallback',
      'useMemo', 'useRef', 'useImperativeHandle', 'useLayoutEffect', 'useDebugValue', 'useId',
      'useTransition', 'useDeferredValue', 'useSyncExternalStore', 'useInsertionEffect',
      // Common identifiers
      'i', 'j', 'k', 'x', 'y', 'z', 'n', 'm', 'a', 'b', 'c', 'e', 'err', 'error', 'data', 'result',
      'value', 'key', 'index', 'item', 'element', 'node', 'el', 'props', 'state', 'context',
    ]);
  }

  private initializeBuiltins(): Map<string, Set<string>> {
    return new Map([
      ['Array', new Set(['from', 'isArray', 'of', 'prototype'])],
      ['Object', new Set(['assign', 'create', 'defineProperty', 'defineProperties', 'entries', 'freeze', 'fromEntries', 'getOwnPropertyDescriptor', 'getOwnPropertyDescriptors', 'getOwnPropertyNames', 'getOwnPropertySymbols', 'getPrototypeOf', 'is', 'isExtensible', 'isFrozen', 'isSealed', 'keys', 'preventExtensions', 'seal', 'setPrototypeOf', 'values', 'prototype'])],
      ['String', new Set(['fromCharCode', 'fromCodePoint', 'raw', 'prototype'])],
      ['Number', new Set(['isFinite', 'isInteger', 'isNaN', 'isSafeInteger', 'parseFloat', 'parseInt', 'EPSILON', 'MAX_SAFE_INTEGER', 'MAX_VALUE', 'MIN_SAFE_INTEGER', 'MIN_VALUE', 'NaN', 'NEGATIVE_INFINITY', 'POSITIVE_INFINITY', 'prototype'])],
      ['Math', new Set(['abs', 'acos', 'acosh', 'asin', 'asinh', 'atan', 'atan2', 'atanh', 'cbrt', 'ceil', 'clz32', 'cos', 'cosh', 'E', 'exp', 'expm1', 'floor', 'fround', 'hypot', 'imul', 'LN10', 'LN2', 'log', 'log10', 'log1p', 'log2', 'LOG10E', 'LOG2E', 'max', 'min', 'PI', 'pow', 'random', 'round', 'sign', 'sin', 'sinh', 'sqrt', 'SQRT1_2', 'SQRT2', 'tan', 'tanh', 'trunc'])],
      ['Promise', new Set(['all', 'allSettled', 'any', 'race', 'reject', 'resolve', 'prototype'])],
      ['JSON', new Set(['parse', 'stringify'])],
      ['Date', new Set(['now', 'parse', 'UTC', 'prototype'])],
      ['console', new Set(['assert', 'clear', 'count', 'countReset', 'debug', 'dir', 'dirxml', 'error', 'group', 'groupCollapsed', 'groupEnd', 'info', 'log', 'profile', 'profileEnd', 'table', 'time', 'timeEnd', 'timeLog', 'timeStamp', 'trace', 'warn'])],
    ]);
  }

  private isKeyword(word: string): boolean {
    const keywords = new Set([
      'break', 'case', 'catch', 'continue', 'debugger', 'default', 'delete', 'do', 'else',
      'finally', 'for', 'function', 'if', 'in', 'instanceof', 'new', 'return', 'switch',
      'this', 'throw', 'try', 'typeof', 'var', 'void', 'while', 'with', 'class', 'const',
      'enum', 'export', 'extends', 'import', 'super', 'implements', 'interface', 'let',
      'package', 'private', 'protected', 'public', 'static', 'yield', 'async', 'await',
      'of', 'as', 'from', 'get', 'set', 'type', 'declare', 'readonly', 'abstract',
      'true', 'false', 'null', 'undefined',
    ]);
    return keywords.has(word);
  }

  private validateImportSource(source: string): boolean {
    // Check if it's a relative import
    if (source.startsWith('.')) {
      const resolvedPath = path.resolve(this.workspaceRoot, source);
      const extensions = ['.ts', '.tsx', '.js', '.jsx', '.json', ''];
      for (const ext of extensions) {
        if (fs.existsSync(resolvedPath + ext)) return true;
        if (fs.existsSync(path.join(resolvedPath, 'index' + ext))) return true;
      }
      return false;
    }
    // For node_modules, we'd need to check package.json or node_modules
    return true; // Assume external packages exist for now
  }

  private isSuspiciousImport(source: string): boolean {
    const suspiciousPatterns = [
      /^react-native-[a-z]+-[a-z]+$/,
      /^@[a-z]+\/[a-z]+-utils$/,
      /^simple-[a-z]+$/,
      /^easy-[a-z]+$/,
      /^super-[a-z]+$/,
      /^mega-[a-z]+$/,
      /^ultra-[a-z]+$/,
      /^fast-[a-z]+$/,
      /^better-[a-z]+$/,
      /^awesome-[a-z]+$/,
    ];
    return suspiciousPatterns.some(p => p.test(source));
  }

  private parseParams(params: string): ParamInfo[] {
    if (!params.trim()) return [];
    return params.split(',').map(p => {
      const parts = p.trim().split(':');
      const namePart = parts[0].trim();
      const rest = namePart.startsWith('...');
      const optional = namePart.includes('?');
      const name = namePart.replace(/[?.]/g, '').trim();
      const type = parts[1]?.trim() || 'any';
      return { name, type, optional, rest, defaultValue: undefined };
    });
  }

  private calculateFunctionComplexity(code: string, startIndex: number): number {
    let complexity = 1;
    let braceCount = 0;
    let started = false;
    
    for (let i = startIndex; i < code.length; i++) {
      const char = code[i];
      if (char === '{') {
        braceCount++;
        started = true;
      } else if (char === '}') {
        braceCount--;
        if (started && braceCount === 0) break;
      }
      
      // Count decision points
      const slice = code.slice(i, i + 10);
      if (/^if\s*\(/.test(slice) || /^else\s+if/.test(slice) || /^for\s*\(/.test(slice) ||
          /^while\s*\(/.test(slice) || /^case\s+/.test(slice) || /^catch\s*\(/.test(slice)) {
        complexity++;
      }
      if (code.slice(i, i + 2) === '&&' || code.slice(i, i + 2) === '||') {
        complexity++;
      }
      if (char === '?' && code[i + 1] !== '.') {
        complexity++;
      }
    }
    
    return complexity;
  }

  private getLocation(code: string, index: number): { line: number; column: number } {
    const lines = code.slice(0, index).split('\n');
    return { line: lines.length, column: lines[lines.length - 1].length + 1 };
  }

  private getLineNumber(code: string, index: number): number {
    return code.slice(0, index).split('\n').length;
  }

  private findSimilarIdentifiers(name: string, scopes: Map<string, ScopeInfo>): string[] {
    const similar: string[] = [];
    const allNames: string[] = [];
    
    for (const scope of scopes.values()) {
      allNames.push(...scope.variables.keys());
      allNames.push(...scope.functions.keys());
      allNames.push(...scope.types.keys());
    }
    
    for (const existing of allNames) {
      if (this.levenshteinDistance(name.toLowerCase(), existing.toLowerCase()) <= 2) {
        similar.push(existing);
      }
    }
    
    return similar.slice(0, 3);
  }

  private levenshteinDistance(a: string, b: string): number {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    
    const matrix: number[][] = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        matrix[i][j] = b[i - 1] === a[j - 1]
          ? matrix[i - 1][j - 1]
          : Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
      }
    }
    
    return matrix[b.length][a.length];
  }

  private looksLikeReference(code: string, name: string): boolean {
    // Check if the identifier is used in a way that suggests it should be defined
    const patterns = [
      new RegExp(`${name}\\s*\\(`),           // Function call
      new RegExp(`${name}\\s*\\.`),           // Property access
      new RegExp(`new\\s+${name}`),           // Constructor
      new RegExp(`:\\s*${name}\\b`),          // Type annotation
      new RegExp(`<${name}>`),                // Generic type
      new RegExp(`extends\\s+${name}`),       // Class extension
      new RegExp(`implements\\s+${name}`),    // Interface implementation
    ];
    return patterns.some(p => p.test(code));
  }

  private guessReferenceType(code: string, name: string): 'variable' | 'function' | 'type' | 'import' {
    if (new RegExp(`${name}\\s*\\(`).test(code)) return 'function';
    if (new RegExp(`:\\s*${name}\\b`).test(code) || /^[A-Z]/.test(name)) return 'type';
    if (new RegExp(`from\\s+['"].*${name}`).test(code)) return 'import';
    return 'variable';
  }

  private inferType(value: string): string {
    value = value.trim();
    if (/^['"`]/.test(value)) return 'string';
    if (/^\d+$/.test(value)) return 'number';
    if (/^\d+\.\d+$/.test(value)) return 'number';
    if (value === 'true' || value === 'false') return 'boolean';
    if (value === 'null') return 'null';
    if (value === 'undefined') return 'undefined';
    if (/^\[/.test(value)) return 'array';
    if (/^\{/.test(value)) return 'object';
    if (/^new\s+\w+/.test(value)) {
      const match = value.match(/^new\s+(\w+)/);
      return match ? match[1] : 'object';
    }
    return 'unknown';
  }

  private typesCompatible(declared: string, inferred: string): boolean {
    if (declared === inferred) return true;
    if (declared === 'any' || inferred === 'unknown') return true;
    if (declared === 'number' && inferred === 'number') return true;
    if (declared === 'string' && inferred === 'string') return true;
    if (declared.includes('[]') && inferred === 'array') return true;
    return false;
  }

  private suggestAlternative(obj: string, method: string): string {
    const alternatives: Record<string, Record<string, string>> = {
      Array: {
        contains: 'Use .includes() instead',
        remove: 'Use .filter() or .splice() instead',
        add: 'Use .push() instead',
        first: 'Use [0] or .at(0) instead',
        last: 'Use .at(-1) instead',
        empty: 'Use .length === 0 instead',
        clear: 'Set array.length = 0 instead',
        unique: 'Use [...new Set(array)] instead',
      },
      String: {
        reverse: 'Use .split(\'\').reverse().join(\'\') instead',
        capitalize: 'Use str[0].toUpperCase() + str.slice(1) instead',
        isEmpty: 'Use str.length === 0 instead',
      },
      Object: {
        isEmpty: 'Use Object.keys(obj).length === 0 instead',
        merge: 'Use Object.assign() or spread {...obj1, ...obj2} instead',
        clone: 'Use {...obj} or structuredClone(obj) instead',
      },
      Promise: {
        delay: 'Use new Promise(r => setTimeout(r, ms)) instead',
        sleep: 'Use new Promise(r => setTimeout(r, ms)) instead',
        wait: 'Use new Promise(r => setTimeout(r, ms)) instead',
      },
    };

    return alternatives[obj]?.[method] || `Check MDN docs for ${obj} methods`;
  }
}

export function createASTAnalyzer(workspaceRoot: string): ASTAnalyzer {
  return new ASTAnalyzer(workspaceRoot);
}
